import { getToken, deleteToken, onMessage } from 'firebase/messaging';
import { api } from './api';
import { getMessagingIfSupported, FIREBASE_VAPID_KEY, firebaseConfig } from '../lib/firebase';

/**
 * Web push via Firebase Cloud Messaging.
 *
 * - App OPEN  : FCM foreground message (onMessage) → dispatches `fcm:message`
 *               so the inbox refetches (the FCM + API realtime model).
 * - App CLOSED: firebase-messaging-sw.js shows a system notification.
 *
 * The FCM token is registered with the backend via POST /api/v1/app-devices/create.
 */
const SW_URL = '/firebase-messaging-sw.js';
const DEVICES_API = '/api/v1/app-devices';
const DEVICE_ID_KEY = 'fcmDeviceId';
const FCM_TOKEN_KEY = 'fcmToken';
// Set when the user turns push OFF here. Distinguishes "deliberately disabled"
// from "no token yet", which otherwise look identical — an absent token was
// enough for NotificationInitializer to silently re-subscribe, so Disable
// undid itself on the next render.
const FCM_DISABLED_KEY = 'fcmPushDisabled';

let foregroundBound = false;

const getOrCreateDeviceId = (): string => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

class PushNotificationService {
  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'Notification' in window
    );
  }

  getPermissionStatus(): NotificationPermission {
    if (typeof Notification === 'undefined') return 'denied';
    return Notification.permission;
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (typeof Notification === 'undefined') return 'denied';
    return Notification.requestPermission();
  }

  async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported in this browser');
    }
    // Pass the Firebase web config (from VITE_FIREBASE_*) to the worker via the
    // registration query string — a service worker can't read import.meta.env.
    const qs = new URLSearchParams(
      Object.entries(firebaseConfig).reduce<Record<string, string>>((acc, [k, v]) => {
        if (v) acc[k] = String(v);
        return acc;
      }, {}),
    ).toString();
    const swUrl = qs ? `${SW_URL}?${qs}` : SW_URL;
    const existing = await navigator.serviceWorker.getRegistration(swUrl);
    if (existing) return existing;
    return navigator.serviceWorker.register(swUrl);
  }

  /** True if we currently hold an FCM token for this device. */
  async checkSubscriptionStatus(): Promise<boolean> {
    return this.isSupported() && !!localStorage.getItem(FCM_TOKEN_KEY);
  }

  /** True when the user explicitly turned push off in this browser. */
  isExplicitlyDisabled(): boolean {
    return localStorage.getItem(FCM_DISABLED_KEY) === '1';
  }

  /** Wire the foreground handler once — re-emits FCM data messages as `fcm:message`. */
  private bindForeground(messaging: import('firebase/messaging').Messaging): void {
    if (foregroundBound) return;
    foregroundBound = true;
    onMessage(messaging, async (payload) => {
      const note = payload.notification || {};
      const data = payload.data || {};
      const title = note.title || data.title || 'Notification';
      const body = note.body || data.body || '';

      // One push, one alert on screen. The page gets it first: the bell in the tab
      // being looked at shows it as a toast and claims it. This used to raise a
      // system notification AND the toast for the same push — two alerts.
      const event = new CustomEvent('fcm:message', { detail: payload, cancelable: true });
      const claimed = !window.dispatchEvent(event);

      // A system notification only when nothing on screen showed it — the visible
      // tab has no bell yet (sign-in, role selection). Never from a hidden tab:
      // Firebase calls onMessage only while some tab is visible, and that tab
      // handles it. (App closed or backgrounded: the service worker shows it.)
      if (claimed || document.visibilityState !== 'visible') return;

      if (Notification.permission === 'granted') {
        try {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(title, {
            body,
            icon: '/rtgs/logo192.png',
            badge: '/rtgs/logo192.png',
            tag: data.notificationId || undefined,
            requireInteraction: data.priority === 'HIGH',
            data: {
              ...data,
              // Same destination as the bell's "View all": the legacy
              // `/rtgs/notifications` has no route and opened NotFound.
              url: data.click_action || '/notification-engine?tab=inbox',
            },
          });
        } catch {
          new Notification(title, {
            body,
            icon: '/rtgs/logo192.png',
            tag: data.notificationId || undefined,
          });
        }
      }
    });
  }

  async subscribe(contactId: string, _token?: string): Promise<string> {
    const messaging = await getMessagingIfSupported();
    if (!messaging) throw new Error('Push notifications are not supported/configured');

    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted');
    }

    const registration = await this.registerServiceWorker();
    await navigator.serviceWorker.ready;

    const fcmToken = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!fcmToken) throw new Error('Failed to obtain an FCM token');

    localStorage.setItem(FCM_TOKEN_KEY, fcmToken);
    localStorage.removeItem(FCM_DISABLED_KEY);
    this.bindForeground(messaging);

    // Registration is idempotent server-side: a known deviceId refreshes its
    // token and reactivates instead of conflicting.
    await api.post(`${DEVICES_API}/create`, {
      userId: contactId,
      deviceId: getOrCreateDeviceId(),
      devicePlatform: 'WEB',
      fcmToken,
      isDeviceActive: true,
    });

    return fcmToken;
  }

  async unsubscribe(contactId: string, _token?: string): Promise<boolean> {
    const messaging = await getMessagingIfSupported();
    try {
      if (messaging) await deleteToken(messaging);
    } catch {
      /* token may already be gone */
    }
    localStorage.removeItem(FCM_TOKEN_KEY);
    localStorage.setItem(FCM_DISABLED_KEY, '1');

    // Tell the server, or it keeps pushing. Dropping the token locally only
    // stops THIS browser from minting it again — the app_devices row stayed
    // active and every send still targeted it. /create is the self-service
    // endpoint (list/update are RBAC-gated) and now updates a known device,
    // so re-posting it with isDeviceActive:false is the deactivation path.
    if (contactId) {
      try {
        await api.post(`${DEVICES_API}/create`, {
          userId: contactId,
          deviceId: getOrCreateDeviceId(),
          devicePlatform: 'WEB',
          fcmToken: null,
          isDeviceActive: false,
        });
      } catch (err) {
        // Local state is already off; surface it but don't fail the toggle.
        console.warn('[push] could not deactivate device server-side:', err);
      }
    }
    return true;
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
